import type { FC } from "react";
import { useEffect } from "react";

import { OUTCOME_ID_QUERY_PARAM, TAB_QUERY_PARAM } from "components/constants";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { SidebarOptionsMenu } from "components/menus/SidebarOptionsMenu";
import { AddOutcome } from "components/outcomes/AddOutcome";
import { displayOutcomeName, outcomesToOptions } from "components/outcomes/helpers";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { ListItem } from "mds/components/ListItem";
import { showConfirmationModal } from "mds/components/Modal";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  NestedSectionOutcomeType,
  outcomeSelectors,
  selectAssessedPageOutcomesBySectionOutcome,
  selectFullSectionOutcomes,
  selectPagesForOutcome,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { CourseContentTabKeys, withQueryParams } from "utils/urls";

interface DeleteOutcomeModalProps {
  onClose: () => void;
  onConfirm: (data?: boolean) => void;
  sectionOutcome: NestedSectionOutcomeType;
  pageUsageCount: number;
}
const DeleteOutcomeModal: FC<DeleteOutcomeModalProps> = ({
  onClose,
  onConfirm,
  pageUsageCount,
  sectionOutcome,
}) => {
  return (
    <div className="mt-2 flex flex-col items-start gap-4">
      <div className="flex flex-col gap-4">
        <div>{t("confirm_delete_modal.delete_label", { name: sectionOutcome.outcome.title })}</div>
        <div className="border-l-2 border-red-tint-20 pl-4 font-semibold">
          {t("confirm_delete_modal.delete_warning_usage", { count: pageUsageCount })}
        </div>
      </div>

      <div className="flex w-full justify-end gap-2">
        <Button kind="white" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </Button>

        <Button kind="red" size="m" title={t("common.delete")} onClick={() => onConfirm(true)}>
          {t("common.delete")}
        </Button>
      </div>
    </div>
  );
};

const handleDeleteOutcome = async (
  sectionOutcome: NestedSectionOutcomeType,
  pageUsageCount: number,
) => {
  let didDelete = false;

  if (pageUsageCount > 0) {
    didDelete = await showConfirmationModal({
      title: t("confirm_delete_modal.delete_item", { item: t("glossary.outcome") }),
      children: (onClose, onConfirm) => (
        <DeleteOutcomeModal
          pageUsageCount={pageUsageCount}
          sectionOutcome={sectionOutcome}
          onClose={onClose}
          onConfirm={onConfirm}
        />
      ),
      hideCancelConfirmButtons: true,
    });
  }

  if (didDelete || pageUsageCount === 0) {
    storeApi.section_outcomes.destroy(sectionOutcome.id);
    trackEvent("Course - Delete outcome", {
      ...BUTTON_EVENT_CATEGORY,
    });
  }
};

type SectionOutcomeSidebarOptionsMenuProps = {
  sectionOutcome: NestedSectionOutcomeType;
};

const SectionOutcomeSidebarOptionsMenu: FC<SectionOutcomeSidebarOptionsMenuProps> = ({
  sectionOutcome,
}) => {
  const { canAuthorCourse } = useCapabilities();

  const assessedPageOutcomes = useListSelector((s) =>
    selectAssessedPageOutcomesBySectionOutcome(s, sectionOutcome.id),
  );
  const pagesForOutcome = useAppSelector((s) => selectPagesForOutcome(s, sectionOutcome.id));
  const topicPages = pagesForOutcome.filter((p) => p.category === "activity_page");
  const assignmentPages = pagesForOutcome.filter((p) => p.category === "assignment");
  const resourcePages = pagesForOutcome.filter((p) => p.category === "course_resource");

  const pageUsageCount = topicPages.length + assignmentPages.length + resourcePages.length;

  if (!canAuthorCourse) {
    return null;
  }

  return (
    <SidebarOptionsMenu
      optionsMenuActions={{
        onDelete: () => handleDeleteOutcome(sectionOutcome, pageUsageCount),
        isDeleteDisabled: assessedPageOutcomes.length > 0,
        deleteTooltipText:
          assessedPageOutcomes.length > 0
            ? t("tooltip.cannot_delete_assessed_section_outcome")
            : undefined,
      }}
    />
  );
};

type OutcomesNavSidebarProps = {
  selectedOutcome?: string;
  sectionId: string;
};

export const OutcomesNavSidebar: FC<OutcomesNavSidebarProps> = ({ selectedOutcome, sectionId }) => {
  const { canAuthorSection, canAuthorCourse } = useCapabilities();

  const sectionOutcomes = useAppSelector(selectFullSectionOutcomes);
  const orgOutcomes = useAppSelector(outcomeSelectors.selectAll);
  // TODO: When course-only outcomes are implemented, we should add it to this list
  const filteredSectionOutcomes = sectionOutcomes.filter(
    (sectionOutcome) => !!sectionOutcome.outcome,
  );

  const sectionOutcomeIds = sectionOutcomes.map((sectionOutcome) => sectionOutcome.outcome_id);
  const selectableOutcomes = orgOutcomes.filter(
    (orgOutcome) => !sectionOutcomeIds.includes(orgOutcome.id),
  );

  const options = outcomesToOptions(selectableOutcomes);

  useEffect(() => {
    storeApi.outcome_assessments.list({ section_id: sectionId });
  }, [sectionId]);

  return (
    <div className="w-full border-r border-black-tint-95 bg-white px-4 py-3 md:w-auto md:basis-[240px]">
      {!canAuthorSection && filteredSectionOutcomes.length === 0 && (
        <div className="body-s w-full py-2 text-black-tint-40">
          {t("outcomes.no_section_outcomes")}
        </div>
      )}
      {filteredSectionOutcomes.map((sectionOutcome) => (
        <ListItem
          className="w-full pl-0"
          isSelected={sectionOutcome.outcome.title === selectedOutcome}
          key={sectionOutcome.outcome.title}
          to={withQueryParams("", {
            [TAB_QUERY_PARAM]: CourseContentTabKeys[1],
            [OUTCOME_ID_QUERY_PARAM]: sectionOutcome.outcome.id,
          })}
        >
          <HoverableToolbar className="w-full" readOnly={!canAuthorSection} disableUnlessEditMode>
            <div
              aria-label={`${t("glossary.outcome")} ${displayOutcomeName(sectionOutcome.outcome)}`}
              className="body-s my-1 w-full cursor-pointer truncate px-2"
              role="listitem"
            >
              {displayOutcomeName(sectionOutcome.outcome)}
            </div>

            <SectionOutcomeSidebarOptionsMenu sectionOutcome={sectionOutcome} />
          </HoverableToolbar>
        </ListItem>
      ))}

      {canAuthorSection && canAuthorCourse && (
        <AddOutcome
          options={options}
          hideUnlessEditMode
          onSave={(orgOutcomeIds: string[]) => {
            orgOutcomeIds.forEach((orgOutcomeId) => {
              storeApi.section_outcomes // TODO: Bulk create
                .create({ section_id: sectionId, outcome_id: orgOutcomeId });
            });
          }}
        />
      )}
    </div>
  );
};
